import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import styles from './index.module.scss';
import cn from 'classnames';
import {
  GetLocalHasSubscribed,
  SetSessionPopupShown,
  GetSessionPopupShown,
  SetLocalHasSubscribed,
  SetPopupStartTime,
  GetPopupStartTime,
} from '~/common/utils/session';
import { hexToRgbA } from '~/common/utils';
import { SvgIcon } from '../ui-elements/svg-icon';
import digitalData from '~/common/services/data-layer-service';
import { submitSubscribeLead } from '~/common/services/subscribe-lead-service';
import { getLocationFromStorage } from '~/common/store/user-location-state';
import { StepOne } from './components/step-one';
import { StepTwo } from './components/step-two';
import { StepThree } from './components/step-three';
import { ISubscribeModal, SubscribeStep, StepTwoValues } from './types';

const pushFormNavigateEvent = (stage: string) =>
  digitalData.push({
    event: '_formNavigate',
    form: { name: 'subscribe to subaru', stage },
  });

const getHeaderPostcode = () => getLocationFromStorage()?.postcode ?? '';

export const SubscribeModal = React.memo(() => {
  const [isPopupActive, setIsPopupActive] = useState(false);
  const [data, setData] = useState<ISubscribeModal | undefined>();
  const [backgroundStyle, setBackgroundStyle] = useState<CSSProperties>();

  const [step, setStep] = useState<SubscribeStep>(1);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const hasLeadSubmittedRef = useRef(false);

  const activatePopup = () => {
    if (!isPopupActive) {
      setIsPopupActive(true);
      SetSessionPopupShown();
      pushFormNavigateEvent('subscribe to subaru');
    }
  };

  const deActivatePopup = () => {
    if (!isPopupActive) return;

    if (step === 2 && !hasLeadSubmittedRef.current) {
      hasLeadSubmittedRef.current = true;
      submitSubscribeLead({
        firstName: 'There',
        lastName: 'Blank',
        email,
        postcode: getHeaderPostcode(),
      })
        .then(() => {
          SetLocalHasSubscribed();
          pushFormNavigateEvent('submitted');
        })
        .catch(() => {
          /* best-effort silent submission on close, nothing to surface to the user */
        });
    }

    SetSessionPopupShown();
    setIsPopupActive(false);
  };

  const handleStepOneNext = (submittedEmail: string) => {
    setEmail(submittedEmail);
    setStep(2);
  };

  const handleStepTwoFinish = (values: StepTwoValues) => {
    hasLeadSubmittedRef.current = true;
    setIsSubmitting(true);
    setSubmitError(false);

    submitSubscribeLead({
      firstName: values.firstName,
      lastName: values.lastName,
      email,
      postcode: getHeaderPostcode(),
    })
      .then(() => {
        SetLocalHasSubscribed();
        pushFormNavigateEvent('submitted');
        setStep(3);
      })
      .catch(() => {
        hasLeadSubmittedRef.current = false;
        setSubmitError(true);
      })
      .finally(() => setIsSubmitting(false));
  };

  useEffect(() => {
    if (!data) return;

    if (!data.backgroundColor) {
      setBackgroundStyle({ color: data.fontColor });
      return;
    }

    if (data.isBackgroundGradient) {
      setBackgroundStyle({
        backgroundImage: `linear-gradient(to top, ${hexToRgbA(
          data.backgroundColor,
          '0'
        )},${data.backgroundColor},${data.backgroundColor},${
          data.backgroundColor
        })`,
        color: data.fontColor,
      });
    } else {
      setBackgroundStyle({
        backgroundColor: data.backgroundColor,
        color: data.fontColor,
      });
    }
  }, [data]);

  useEffect(() => {
    const _data: ISubscribeModal = window['SubscribePopupModel'];
    if (_data) setData(_data);
  }, []);

  useEffect(() => {
    if (!data) return;
    const blockedPaths = [
      '/contact-us',
      '/fleet',
      '/value-my-car',
      '/buy-online',
      '/service-booking',
      '/dealers',
      '/book-test-drive',
      '/brochure-download',
      '/special-offers',
      '/configure',
    ];
    const currentPath = window.location.pathname.toLowerCase();
    const isBlocked = blockedPaths.some((p) =>
      currentPath.startsWith(p.toLowerCase())
    );

    if (GetLocalHasSubscribed() || GetSessionPopupShown() || isBlocked) return;

    SetPopupStartTime();
    const startTime = GetPopupStartTime();
    const elapsed = Date.now() - startTime;

    const tryActivatePopup = () => {
      if (GetLocalHasSubscribed() || GetSessionPopupShown()) return;
      activatePopup();
    };

    const delay = 30000;
    if (elapsed >= delay) {
      tryActivatePopup();
    } else {
      const timer = setTimeout(tryActivatePopup, delay - elapsed);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <div
      className={cn(styles.SubscribeNowPopup, {
        [styles.Active]: isPopupActive,
        [styles.Inactive]: !isPopupActive,
      })}
    >
      {data && (
        <div className={styles.MainWrapper}>
          <div className={styles.Overlay} onClick={deActivatePopup} />
          <div className={styles.Wrapper}>
            <div
              className={styles.ImageSide}
              style={{ backgroundImage: `url(${data.backgroundImage})` }}
            />
            <div className={styles.Container}>
              <div className={styles.Close} onClick={deActivatePopup}>
                <SvgIcon type="close" size={1.5} />
              </div>
              <div className={styles.Content} style={backgroundStyle}>
                {step === 1 && (
                  <StepOne
                    title={data.title}
                    content={data.content}
                    privacyCheckbox={data.privacyCheckbox}
                    privacyDropDown={data.privacyDropDown}
                    fontColor={data.fontColor}
                    contentAlignment={data.contentAlignment}
                    onNext={handleStepOneNext}
                  />
                )}
                {step === 2 && (
                  <StepTwo
                    fontColor={data.fontColor}
                    isSubmitting={isSubmitting}
                    submitError={submitError}
                    onFinish={handleStepTwoFinish}
                  />
                )}
                {step === 3 && <StepThree fontColor={data.fontColor} />}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
